import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  type TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import type { BrandConfig, EnvField, EnvGroup, TenantAnswers } from '@api/api';
import { GROUP_LABEL, GROUP_ORDER, isRelevant, isRequired, missingRequired, type Errors } from '../lib/validate';

const MASKED = '••••';

// ------------------------------------------------------------------ tenant

const CURRENCIES = ['EUR', 'USD', 'GBP', 'UAH', 'PLN', 'CZK', 'GEL', 'RON', 'HUF', 'TRY', 'KZT', 'AED', 'CHF', 'SEK', 'NOK', 'DKK'];
const LOCALES = ['en', 'uk', 'pl', 'de', 'fr', 'es', 'it', 'pt', 'ro', 'cs', 'ka', 'tr', 'kk', 'ar'];
const COUNTRIES = ['GB', 'IE', 'UA', 'PL', 'DE', 'FR', 'ES', 'IT', 'PT', 'RO', 'CZ', 'GE', 'TR', 'KZ', 'AE', 'US'];

function timezones(): string[] {
  try {
    const fn = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (fn) return fn('timeZone');
  } catch {
    /* old browser */
  }
  return ['Europe/London', 'Europe/Kyiv', 'Europe/Warsaw', 'Europe/Berlin', 'Europe/Tbilisi', 'Europe/Istanbul', 'Asia/Dubai', 'America/New_York'];
}

const num = (s: string): number | undefined => (s.trim() === '' ? undefined : Number(s.replace(',', '.')));

/** Decimal input that keeps what is typed ("41." stays "41.") and reports a number (undefined when empty). */
export function NumberField({ value, onChange, ...rest }: Omit<TextFieldProps, 'value' | 'onChange'> & { value: number | undefined; onChange: (v: number | undefined) => void }) {
  const [text, setText] = useState(value === undefined || Number.isNaN(value) ? '' : String(value));
  useEffect(() => {
    if (num(text) !== value && !(value === undefined && text === '')) setText(value === undefined || Number.isNaN(value) ? '' : String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <TextField
      {...rest}
      value={text}
      onChange={(e) => {
        const t = e.target.value.replace(',', '.');
        if (t !== '' && !/^-?\d*\.?\d*$/.test(t)) return;
        setText(t);
        const n = num(t);
        onChange(n === undefined || Number.isNaN(n) ? undefined : n);
      }}
      inputProps={{ inputMode: 'decimal', ...rest.inputProps }}
    />
  );
}

export function TenantForm({ value, onChange, errors = {}, disabled, showErrors = true }: { value: TenantAnswers; onChange: (t: TenantAnswers) => void; errors?: Errors; disabled?: boolean; showErrors?: boolean }) {
  const tz = useMemo(timezones, []);
  const err = (k: string) => (showErrors && errors[k] ? { error: true, helperText: errors[k] } : {});
  const set = (patch: Partial<TenantAnswers>) => onChange({ ...value, ...patch });
  const setCity = (patch: Partial<TenantAnswers['city']>) => onChange({ ...value, city: { ...value.city, ...patch } });
  const setPrice = (k: keyof TenantAnswers['prices'], v: number | undefined) => onChange({ ...value, prices: { ...value.prices, [k]: v } });

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Brand and market
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Brand name" value={value.brandName} onChange={(e) => set({ brandName: e.target.value })} disabled={disabled} {...err('brandName')} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Autocomplete freeSolo options={CURRENCIES} value={value.currency} disabled={disabled} onInputChange={(_, v) => set({ currency: v.toUpperCase() })} renderInput={(p) => <TextField {...p} label="Currency" {...err('currency')} />} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Autocomplete freeSolo options={COUNTRIES} value={value.defaultCountry} disabled={disabled} onInputChange={(_, v) => set({ defaultCountry: v.toUpperCase() })} renderInput={(p) => <TextField {...p} label="Country" {...err('defaultCountry')} />} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete options={tz} value={value.timezone || null} disabled={disabled} onChange={(_, v) => set({ timezone: v ?? '' })} renderInput={(p) => <TextField {...p} label="Time zone" {...err('timezone')} />} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              freeSolo
              options={COUNTRIES}
              value={value.phoneCountries}
              disabled={disabled}
              onChange={(_, v) => set({ phoneCountries: v.map((x) => x.toUpperCase()) })}
              renderInput={(p) => <TextField {...p} label="Phone countries" {...(err('phoneCountries').error ? err('phoneCountries') : { helperText: 'Countries riders can sign up with' })} />}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Autocomplete freeSolo options={LOCALES} value={value.defaultLocale} disabled={disabled} onInputChange={(_, v) => set({ defaultLocale: v.toLowerCase() })} renderInput={(p) => <TextField {...p} label="Default locale" {...err('defaultLocale')} />} />
          </Grid>
          <Grid item xs={12} sm={9}>
            <Autocomplete multiple freeSolo options={LOCALES} value={value.locales} disabled={disabled} onChange={(_, v) => set({ locales: v.map((x) => x.toLowerCase()) })} renderInput={(p) => <TextField {...p} label="Locales" {...err('locales')} />} />
          </Grid>
        </Grid>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          First city
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="City name" value={value.city.name} onChange={(e) => setCity({ name: e.target.value })} disabled={disabled} {...err('city.name')} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <NumberField fullWidth label="Latitude" value={value.city.lat || undefined} onChange={(v) => setCity({ lat: v ?? 0 })} disabled={disabled} {...err('city.lat')} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <NumberField fullWidth label="Longitude" value={value.city.lon || undefined} onChange={(v) => setCity({ lon: v ?? 0 })} disabled={disabled} {...err('city.lon')} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth label="Short code" value={value.city.shortCode} onChange={(e) => setCity({ shortCode: e.target.value.toUpperCase() })} disabled={disabled} {...err('city.shortCode')} />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField fullWidth label="City locale" value={value.city.locale} onChange={(e) => setCity({ locale: e.target.value.toLowerCase() })} disabled={disabled} />
          </Grid>
          {value.city.lat !== 0 && value.city.lon !== 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Check the point:{' '}
                <a href={`https://www.openstreetmap.org/?mlat=${value.city.lat}&mlon=${value.city.lon}#map=12/${value.city.lat}/${value.city.lon}`} target="_blank" rel="noreferrer">
                  open in OpenStreetMap
                </a>
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Prices ({value.currency || 'currency'})
        </Typography>
        <Grid container spacing={2}>
          {(
            [
              ['unlock', 'Unlock'],
              ['rideMinute', 'Ride, per minute'],
              ['pauseMinute', 'Pause, per minute'],
              ['reservationMinute', 'Reservation, per minute'],
            ] as const
          ).map(([k, label]) => (
            <Grid item xs={6} sm={3} key={k}>
              <NumberField fullWidth label={label} value={value.prices[k]} onChange={(v) => setPrice(k, v)} disabled={disabled} {...err(`prices.${k}`)} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          First super admin
        </Typography>
        <TextField
          fullWidth
          label="Admin login"
          value={value.adminLogin}
          onChange={(e) => set({ adminLogin: e.target.value.trim() })}
          disabled={disabled}
          {...(err('adminLogin').error ? err('adminLogin') : { helperText: 'The password is generated during provisioning and shown once.' })}
          sx={{ maxWidth: 480 }}
        />
      </Box>
    </Stack>
  );
}

// ------------------------------------------------------------------ brand

export function BrandForm({ value, onChange, errors = {}, showErrors = true }: { value: BrandConfig; onChange: (b: BrandConfig) => void; errors?: Errors; showErrors?: boolean }) {
  const err = (k: string) => (showErrors && errors[k] ? { error: true, helperText: errors[k] } : {});
  const set = (patch: Partial<BrandConfig>) => onChange({ ...value, ...patch });
  const color = (k: 'primary' | 'onPrimary', label: string) => (
    <TextField
      fullWidth
      label={label}
      value={value.colors[k]}
      onChange={(e) => onChange({ ...value, colors: { ...value.colors, [k]: e.target.value } })}
      {...err(`colors.${k}`)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box component="input" type="color" value={/^#[0-9a-f]{6}$/i.test(value.colors[k]) ? value.colors[k] : '#000000'} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, colors: { ...value.colors, [k]: e.target.value.toUpperCase() } })} aria-label={`${label} picker`} sx={{ width: 28, height: 28, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }} />
          </InputAdornment>
        ),
      }}
    />
  );
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="App name" value={value.appName} onChange={(e) => set({ appName: e.target.value })} {...err('appName')} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Apple team ID" value={value.appleTeamId} onChange={(e) => set({ appleTeamId: e.target.value.toUpperCase() })} {...err('appleTeamId')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Rider bundle ID (iOS)" value={value.riderBundleId} onChange={(e) => set({ riderBundleId: e.target.value.trim() })} {...err('riderBundleId')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Staff bundle ID (iOS)" value={value.staffBundleId} onChange={(e) => set({ staffBundleId: e.target.value.trim() })} {...err('staffBundleId')} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField fullWidth label="Android package" value={value.androidPackage} onChange={(e) => set({ androidPackage: e.target.value.trim() })} {...err('androidPackage')} />
      </Grid>
      <Grid item xs={6} sm={3}>
        {color('primary', 'Primary colour')}
      </Grid>
      <Grid item xs={6} sm={3}>
        {color('onPrimary', 'Text on primary')}
      </Grid>
      <Grid item xs={12} sm={6}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
          <Box sx={{ px: 2.5, py: 1, borderRadius: 2, bgcolor: value.colors.primary, color: value.colors.onPrimary, fontWeight: 700 }}>{value.appName || 'Start ride'}</Box>
          <Typography variant="caption" color="text.secondary">
            preview
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Support e-mail" value={value.supportEmail ?? ''} onChange={(e) => set({ supportEmail: e.target.value })} {...err('supportEmail')} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Support phone" value={value.supportPhone ?? ''} onChange={(e) => set({ supportPhone: e.target.value })} />
      </Grid>
    </Grid>
  );
}

// ------------------------------------------------------------------ env

function EnvInput({
  f,
  value,
  edited,
  required,
  missing,
  onChange,
  onReset,
  onReveal,
}: {
  f: EnvField;
  value: string | null;
  edited: boolean;
  required: boolean;
  missing: boolean;
  onChange: (v: string | null) => void;
  onReset: () => void;
  onReveal?: () => Promise<void>;
}) {
  const [revealing, setRevealing] = useState(false);
  const masked = !edited && value === MASKED;
  const multi = f.type === 'json' || f.type === 'file';
  const label = (
    <span>
      {f.label}
      {required && ' *'}
    </span>
  );
  const helper = missing ? `Required — not set${f.help ? `. ${f.help}` : ''}` : f.help || ' ';
  const placeholder = masked ? '•••• set — type to replace' : f.default ? `default: ${f.default}` : multi ? 'Paste JSON or upload a file' : undefined;

  const reveal = masked && onReveal && (
    <Tooltip title="Reveal (2FA)">
      <span>
        <IconButton
          size="small"
          aria-label={`Reveal ${f.key}`}
          disabled={revealing}
          onClick={async () => {
            setRevealing(true);
            try {
              await onReveal();
            } finally {
              setRevealing(false);
            }
          }}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  if (f.generated && !edited) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
        <AutoAwesomeOutlinedIcon fontSize="small" color="primary" />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {f.label}{' '}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {f.key}
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-all' }}>
            Generated automatically{value && value !== MASKED ? `: ${value}` : ''}
            {f.locked ? ' · fixed after provisioning' : ''}
          </Typography>
        </Box>
        {reveal}
        {!f.locked && (
          <Button size="small" onClick={() => onChange('')}>
            Change
          </Button>
        )}
      </Stack>
    );
  }

  const reset = edited && (
    <Tooltip title="Undo change">
      <IconButton size="small" onClick={onReset} aria-label={`Undo ${f.key}`}>
        <UndoIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  if (f.type === 'bool') {
    const v = value ?? f.default ?? 'false';
    return (
      <Box>
        <Stack direction="row" alignItems="center">
          <FormControlLabel control={<Switch checked={v === 'true' || v === '1'} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />} label={label} />
          {reset}
        </Stack>
        {f.help && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 6, mt: -0.5 }}>
            {f.help}
          </Typography>
        )}
      </Box>
    );
  }

  if (f.type === 'select') {
    return (
      <TextField select fullWidth label={label} value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} error={missing} helperText={helper} InputProps={{ endAdornment: reset || undefined }} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
        <MenuItem value="">
          <em>{f.default ? `not set (default: ${f.default})` : 'not set'}</em>
        </MenuItem>
        {(f.options ?? [])
          .filter((o) => o !== '')
          .map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
      </TextField>
    );
  }

  const fileLoad = (file: File) => {
    const r = new FileReader();
    r.onload = () => onChange(String(r.result));
    r.readAsText(file);
  };

  return (
    <TextField
      fullWidth
      label={label}
      value={masked ? '' : value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      type={f.type === 'secret' && !edited ? 'password' : 'text'}
      multiline={multi && !masked}
      minRows={multi && !masked ? 2 : undefined}
      maxRows={8}
      error={missing}
      helperText={helper}
      InputLabelProps={placeholder ? { shrink: true } : undefined}
      inputProps={{ autoComplete: 'off', spellCheck: false, 'data-key': f.key, inputMode: f.type === 'number' ? 'decimal' : undefined, style: f.type === 'secret' || multi ? { fontFamily: 'monospace' } : undefined }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {reset}
            {multi && (
              <Tooltip title="Upload file">
                <IconButton component="label" size="small" aria-label={`Upload ${f.key}`}>
                  <UploadFileOutlinedIcon fontSize="small" />
                  <input hidden type="file" accept=".json,.plist,application/json" onChange={(e) => e.target.files?.[0] && fileLoad(e.target.files[0])} />
                </IconButton>
              </Tooltip>
            )}
            {reveal}
          </InputAdornment>
        ),
      }}
    />
  );
}

/**
 * The .env editor, grouped by EnvGroup. `edits` holds local changes (key → value, null = clear);
 * `revealed` holds values fetched with 2FA (shown, not edits); `serverMissing` is CloneDetail.missingRequired.
 */
export function EnvForm({
  fields,
  edits,
  onEdit,
  onReveal,
  revealed = {},
  groups,
  serverMissing,
}: {
  fields: EnvField[];
  edits: Record<string, string | null>;
  onEdit: (key: string, value: string | null | undefined) => void;
  onReveal?: (key: string) => Promise<void>;
  revealed?: Record<string, string>;
  groups?: EnvGroup[];
  serverMissing?: string[];
}) {
  const [advanced, setAdvanced] = useState(false);
  const order = groups ?? GROUP_ORDER;
  const missing = useMemo(() => new Set(missingRequired(fields, edits, serverMissing)), [fields, edits, serverMissing]);
  const byGroup = useMemo(() => {
    const m = new Map<EnvGroup, EnvField[]>();
    for (const g of order) m.set(g, []);
    for (const f of fields) m.get(f.group)?.push(f);
    return m;
  }, [fields, order]);
  // a missing advanced field must not hide behind the toggle
  const isAdvanced = (f: EnvField) => (f.advanced || f.group === 'advanced') && !missing.has(f.key);
  const advancedCount = fields.filter(isAdvanced).length;

  return (
    <Stack spacing={2}>
      {order.map((g) => {
        const list = (byGroup.get(g) ?? []).filter((f) => isRelevant(f, fields, edits));
        const basic = list.filter((f) => !isAdvanced(f));
        const adv = list.filter(isAdvanced);
        if (!basic.length && !(advanced && adv.length)) return null;
        const missingCount = list.filter((f) => missing.has(f.key)).length;
        const renderField = (f: EnvField) => {
          const edited = f.key in edits;
          const shown = !edited && revealed[f.key] !== undefined;
          const v = edited ? edits[f.key] : shown ? revealed[f.key] : f.value;
          return (
            <Grid item xs={12} md={f.type === 'json' || f.type === 'file' ? 12 : 6} key={f.key}>
              <EnvInput
                f={shown && f.type === 'secret' ? { ...f, type: 'string' } : f}
                value={v}
                edited={edited}
                required={isRequired(f, fields, edits)}
                missing={missing.has(f.key)}
                onChange={(val) => onEdit(f.key, val)}
                onReset={() => onEdit(f.key, undefined)}
                onReveal={onReveal ? () => onReveal(f.key) : undefined}
              />
            </Grid>
          );
        };
        return (
          <Card key={g} sx={{ borderColor: missingCount ? 'warning.main' : undefined }} data-testid={`env-group-${g}`}>
            <Box sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {GROUP_LABEL[g].title}
                </Typography>
                {missingCount > 0 && <Chip size="small" color="warning" label={`${missingCount} missing`} />}
              </Stack>
              {GROUP_LABEL[g].help && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {GROUP_LABEL[g].help}
                </Typography>
              )}
              <Grid container spacing={2}>
                {basic.map(renderField)}
              </Grid>
              <Collapse in={advanced && adv.length > 0} unmountOnExit>
                <Grid container spacing={2} sx={{ mt: basic.length ? 0 : -2 }}>
                  {adv.map(renderField)}
                </Grid>
              </Collapse>
            </Box>
          </Card>
        );
      })}
      {advancedCount > 0 && (
        <Box>
          <Button size="small" onClick={() => setAdvanced((a) => !a)}>
            {advanced ? 'Hide advanced fields' : `Show advanced fields (${advancedCount})`}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
